import { useMemo } from 'react'
import {
  selectCartTotal,
  selectConfig,
  selectOrder,
  useAppSelector,
} from '@open-tender/cloud'
import type { ServiceType } from '@open-tender/types'
import {
  HolidayHours,
  ProjectedPickupResult,
  projectPickupTime,
} from 'utils/projectedPickup'

/**
 * Zero-network client-side check: recomputes whenever the cart total or
 * order context changes (the same redux state the quantity controls in
 * CartItem/MenuItemQuantity already mutate), never on a timer or an API
 * response. Advisory only -- the server remains authoritative at submit.
 */
export const useProjectedPickup = (): ProjectedPickupResult => {
  const cartSubtotal = useAppSelector(selectCartTotal)
  const { revenueCenter, serviceType, requestedAt } =
    useAppSelector(selectOrder)
  // Not on the ConfigBrand type in the installed @open-tender/types tarball.
  // Absent (older backend) reads as off, which fails open.
  const sendDynamicPrepTime = Boolean(
    (
      useAppSelector(selectConfig)?.brand as {
        send_dynamic_prep_time?: boolean
      }
    )?.send_dynamic_prep_time
  )

  return useMemo(
    () =>
      projectPickupTime({
        cartSubtotal,
        dynamicPrepTimes: revenueCenter?.dynamic_prep_times,
        hours: revenueCenter?.hours,
        // Not on the RevenueCenter type in the installed @open-tender/types
        // tarball, but the single-RC order-api payload does include it.
        holidays: (revenueCenter as { holidays?: HolidayHours } | null)
          ?.holidays,
        baseWaitTimes: (
          revenueCenter as {
            base_wait_times?: Partial<Record<ServiceType, number | null>>
          } | null
        )?.base_wait_times,
        sendDynamicPrepTime,
        timezone: revenueCenter?.timezone,
        serviceType,
        requestedAt,
      }),
    // revenueCenter's identity changes whenever any of the fields read above
    // change, so listing them individually as well would be redundant.
    [cartSubtotal, revenueCenter, serviceType, requestedAt, sendDynamicPrepTime]
  )
}
